/**
 * Profile pictures are stored inline as data URLs and sent with every session
 * load, so they are cropped to a centred square and scaled down to at most
 * 256 px before upload. The result is a JPEG of a few tens of KB.
 */
export const AVATAR_SIZE = 256;
export const AVATAR_ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
// The source file only has to be decodable; the uploaded picture is resized.
export const AVATAR_MAX_SOURCE_BYTES = 10 * 1024 * 1024;

/** The largest centred square inside a width x height image. */
export const centredSquare = (width, height) => {
  const side = Math.min(width, height);
  return {
    sx: Math.round((width - side) / 2),
    sy: Math.round((height - side) / 2),
    side,
  };
};

const loadImage = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    URL.revokeObjectURL(url);
    resolve(image);
  };
  image.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new Error('Unreadable image'));
  };
  image.src = url;
});

/**
 * Resize an image file to a square JPEG data URL of at most `size` px.
 * Smaller pictures are not scaled up. Transparent areas become white, as
 * JPEG has no transparency.
 */
export const resizeImageToDataUrl = async (file, size = AVATAR_SIZE) => {
  const image = await loadImage(file);
  const { sx, sy, side } = centredSquare(image.naturalWidth, image.naturalHeight);
  const target = Math.min(size, side);

  const canvas = document.createElement('canvas');
  canvas.width = target;
  canvas.height = target;
  const context = canvas.getContext('2d');
  if (!context || !target) throw new Error('Unreadable image');

  context.fillStyle = '#FFFFFF';
  context.fillRect(0, 0, target, target);
  context.imageSmoothingQuality = 'high';
  context.drawImage(image, sx, sy, side, side, 0, 0, target, target);
  return canvas.toDataURL('image/jpeg', 0.85);
};
